<template>
  <div class="fu-more-root">
    <div class="fu-more__scrim" data-test="fu-more-filters-scrim" @click="$emit('close')"></div>
    <aside
      ref="sheet"
      class="fu-more"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      data-test="fu-more-filters-sheet"
    >
      <div class="fu-more__head">
        <div class="fu-more__titles">
          <h2 class="fu-more__title">{{ title }}</h2>
          <p v-if="subtitle" class="fu-more__subtitle">{{ subtitle }}</p>
        </div>
        <button type="button" class="fu-more__close" aria-label="Close" data-test="fu-more-close" @click="$emit('close')">
          <fu-icon name="x" :size="18" />
        </button>
      </div>

      <div class="fu-more__body">
        <section
          v-for="s in sections"
          :key="s.key"
          class="fu-more__section"
          :data-test="`fu-more-section-${s.key}`"
        >
          <div class="fu-more__section-head">
            <div class="fu-more__section-titles">
              <div class="fu-more__section-title" :id="`${uid}-${s.key}`">{{ s.title }}</div>
              <div v-if="s.hint" class="fu-more__hint">{{ s.hint }}</div>
            </div>
            <div v-if="s.modes && s.modes.length" class="fu-more__modes" role="group" :aria-label="`${s.title}: include or exclude`">
              <button
                v-for="m in s.modes"
                :key="m.value"
                type="button"
                class="fu-more__mode"
                :class="{ 'fu-more__mode--on': draft[s.key].mode === m.value }"
                :aria-pressed="draft[s.key].mode === m.value ? 'true' : 'false'"
                :data-test="`fu-more-mode-${s.key}-${m.value}`"
                @click="draft[s.key].mode = m.value"
              >{{ m.label }}</button>
            </div>
          </div>

          <template v-if="s.input">
            <div class="fu-more__affix" :class="{ 'fu-more__affix--invalid': !!inputErrors[s.key] }">
              <span v-if="s.input.prefix" class="fu-more__affix-part">{{ s.input.prefix }}</span>
              <input
                type="text"
                inputmode="numeric"
                class="fu-more__affix-input"
                :value="draft[s.key].text"
                :placeholder="s.input.placeholder || 'Any'"
                :aria-labelledby="`${uid}-${s.key}`"
                :aria-invalid="inputErrors[s.key] ? 'true' : 'false'"
                :data-test="`fu-more-input-${s.key}`"
                @input="onNumberInput(s, $event)"
              />
              <span v-if="s.input.suffix" class="fu-more__affix-part fu-more__affix-part--end">{{ s.input.suffix }}</span>
            </div>
            <div v-if="inputErrors[s.key]" class="fu-more__error" role="alert" :data-test="`fu-more-error-${s.key}`">{{ inputErrors[s.key] }}</div>
          </template>

          <template v-if="s.options">
            <div v-if="groupsOf(s).length" class="fu-more__groups" role="group" :aria-label="`${s.title}: quick picks`">
              <button
                v-for="g in groupsOf(s)"
                :key="g.key || g.label"
                type="button"
                class="fu-more__group"
                :class="{ 'fu-more__group--on': groupOn(s.key, g) }"
                :aria-pressed="groupOn(s.key, g) ? 'true' : 'false'"
                :data-test="`fu-more-group-${s.key}`"
                @click="toggleGroup(s.key, g)"
              >{{ g.label }}</button>
            </div>
            <label v-if="hasSearch(s)" class="fu-more__search">
              <span class="fu-more__sr">Search {{ s.title }}</span>
              <fu-icon name="search" :size="15" class="fu-more__search-icon" />
              <input
                type="search"
                class="fu-more__search-input"
                :placeholder="s.searchPlaceholder || `Search ${s.title.toLowerCase()}…`"
                :value="queries[s.key] || ''"
                autocomplete="off"
                :data-test="`fu-more-search-${s.key}`"
                @input="$set(queries, s.key, $event.target.value)"
                @keydown.enter.prevent="onSearchEnter(s)"
              />
            </label>
            <button
              v-if="wildcardPrefix(s) && matchedOptions(s).length"
              type="button"
              class="fu-more__action"
              :data-test="`fu-more-wildcard-${s.key}`"
              @click="pickWildcard(s)"
            >
              <fu-icon name="check" :size="15" />
              Tick all {{ matchedOptions(s).length }} starting with “{{ wildcardPrefix(s) }}”
            </button>
            <button
              v-if="createText(s)"
              type="button"
              class="fu-more__action"
              :data-test="`fu-more-create-${s.key}`"
              @click="createOption(s)"
            >
              <fu-icon name="plus" :size="15" />
              Add “{{ createText(s) }}”
            </button>
            <div class="fu-more__options" :class="{ 'fu-more__options--scroll': optionsOf(s).length > searchThreshold }">
              <label
                v-for="o in shownOptions(s)"
                :key="String(o.value)"
                class="fu-more__option"
                data-test="fu-more-option"
              >
                <input
                  type="checkbox"
                  class="fu-more__check"
                  :checked="isPicked(s.key, o.value)"
                  @change="togglePick(s.key, o.value)"
                />
                <span class="fu-more__option-label">{{ o.label }}</span>
                <span v-if="o.count !== undefined && o.count !== null" class="fu-more__count">{{ o.count }}</span>
              </label>
              <div v-if="!optionsOf(s).length" class="fu-more__empty">{{ s.emptyText || 'Nothing to pick from.' }}</div>
              <div v-else-if="!shownOptions(s).length" class="fu-more__empty">No matches.</div>
              <div v-if="hiddenCount(s)" class="fu-more__empty" :data-test="`fu-more-capped-${s.key}`">
                Showing {{ shownOptions(s).length }} of {{ matchedOptions(s).length }} — search to find the rest.
              </div>
            </div>
          </template>
        </section>
      </div>

      <div class="fu-more__foot">
        <button type="button" class="fu-more__apply" :disabled="hasErrors" data-test="fu-more-apply" @click="apply">Apply filters</button>
        <button type="button" class="fu-more__cancel" data-test="fu-more-cancel" @click="$emit('close')">Cancel</button>
        <button type="button" class="fu-more__reset" data-test="fu-more-reset" @click="reset">Reset</button>
      </div>
    </aside>
  </div>
</template>

<script>
/**
 * "More filters" — the right-hand sheet (the export's lines 700–749). Data-driven so every
 * follow-up page can describe its own filters:
 *
 *   sections  [{ key, title, hint?,
 *                input?:   { prefix?, suffix?, placeholder?, min?, max?, errorText? }   a whole number
 *                modes?:   [{ value, label }]     include / exclude (a segmented switch)
 *                options?: [{ value, label, count? }], searchable?, searchPlaceholder?, emptyText?,
 *                groups?:  [{ key?, label, values, mode? }]   quick-pick chips (below)
 *                creatable?: { pattern?, uppercase? }        typed values (below)
 *                wildcard?:  true                            "D27*" (below)
 *                renderLimit?: number }]                     long lists (below)
 *   value     { [key]: { text?, mode?, selected? } } — what is applied now; the sheet edits a COPY
 *
 * Long pick lists (Unscheduled Treatment's procedure codes — every code in Open Dental) can opt in
 * to four extras, all off unless the section asks:
 *   groups       chips that tick a set of values at once, or untick it when all are ticked (the
 *                office's custom code categories). A group with `mode` shows only while that mode
 *                is picked (the saved include / exclude codes). Every value must be an option.
 *   creatable    typing a value no option has offers "Add “X”" (or Enter): it becomes a ticked
 *                option and is applied like any other. `pattern` must pass (it gates what can reach
 *                a query); `uppercase` normalises it first.
 *   wildcard     a search ending in * ("D27*") lists the options whose VALUE starts with the rest
 *                and offers "Tick all N" (the old page's search-box wildcard).
 *   renderLimit  draw at most this many options (the ones applied when the sheet opened first),
 *                with "Showing N of M — search to find the rest."
 *
 * Apply emits apply({ [key]: { text, mode, selected } }) — the page turns that back into its own
 * models and re-runs its query, as its old filter panel's Apply button did. Reset clears the draft
 * (text '', nothing ticked, the first mode) but applies nothing until Apply. Cancel, Escape, the ×
 * and a click on the dimmed page emit close.
 *
 * Numbers: only digits can be typed (the export's rule); `min` / `max` show `errorText` and
 * disable Apply. Long option lists (carriers) get a search box and scroll inside the section.
 *
 * Layout: fixed to the right, min(440px, 95%) wide; full width below 640px, with the footer's
 * buttons on the LEFT — the floating agent button (z 9999) owns the bottom-right corner.
 * z between the app header (1030) and Bootstrap modals (1040).
 */
import FuIcon from './FuIcon.vue';

let uidSeq = 0;

function copyValue(sections, value) {
  const out = {};
  (sections || []).forEach((s) => {
    const v = (value && value[s.key]) || {};
    out[s.key] = {
      text: v.text === undefined || v.text === null ? '' : String(v.text),
      mode: v.mode || (s.modes && s.modes.length ? s.modes[0].value : null),
      selected: Array.isArray(v.selected) ? v.selected.slice() : [],
    };
  });
  return out;
}

/** The values each section had applied when the sheet opened, as strings. */
function pinnedValues(sections, value) {
  const out = {};
  (sections || []).forEach((s) => {
    const v = (value && value[s.key]) || {};
    out[s.key] = Array.isArray(v.selected) ? v.selected.map(String) : [];
  });
  return out;
}

export default {
  name: 'FollowupsMoreFilters',
  components: { FuIcon },
  props: {
    title: { type: String, default: 'More filters' },
    subtitle: { type: String, default: 'These run against Open Dental when you apply them.' },
    sections: { type: Array, default: () => [] },
    value: { type: Object, default: () => ({}) },
    searchThreshold: { type: Number, default: 8 },
  },

  data() {
    uidSeq += 1;
    return {
      uid: `fu-more-${uidSeq}`,
      draft: copyValue(this.sections, this.value),
      queries: {},
      added: {},                                    // { [key]: [{ value, label }] } typed with `creatable`
      pinned: pinnedValues(this.sections, this.value), // listed first under a renderLimit
    };
  },

  computed: {
    // NOT `errors`: the app installs vee-validate globally (main.js), which puts its own `errors`
    // ErrorBag on EVERY component and wins over a computed of that name — Apply was stuck disabled.
    inputErrors() {
      const out = {};
      this.sections.forEach((s) => {
        if (!s.input) return;
        const t = this.draft[s.key].text;
        if (t === '') return;
        const n = Number(t);
        const lo = s.input.min;
        const hi = s.input.max;
        if (!/^\d+$/.test(t) || (lo !== undefined && n < lo) || (hi !== undefined && n > hi)) {
          out[s.key] = s.input.errorText || 'Enter a whole number.';
        }
      });
      return out;
    },
    hasErrors() {
      return Object.keys(this.inputErrors).length > 0;
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onKey, true);
    this.$nextTick(() => {
      const sheet = this.$refs.sheet;
      const first = sheet && sheet.querySelector('.fu-more__body input, .fu-more__body button');
      if (first && typeof first.focus === 'function') first.focus({ preventScroll: true });
    });
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKey, true);
  },

  methods: {
    onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation();
        this.$emit('close');
      }
    },
    onNumberInput(s, e) {
      const digits = String(e.target.value).replace(/\D/g, '');
      this.draft[s.key].text = digits;
      if (e.target.value !== digits) e.target.value = digits;
    },
    /** The section's options plus any typed in with `creatable`. */
    optionsOf(s) {
      const extra = this.added[s.key] || [];
      return extra.length ? s.options.concat(extra) : s.options;
    },
    queryOf(s) {
      return String(this.queries[s.key] || '').trim();
    },
    hasSearch(s) {
      return !!s.searchable && (this.optionsOf(s).length > this.searchThreshold || !!s.creatable || !!s.wildcard);
    },
    /** "D27*" → "D27" when the section takes a wildcard; '' otherwise. */
    wildcardPrefix(s) {
      if (!s.wildcard) return '';
      const q = this.queryOf(s);
      return /^[^*]+\*$/.test(q) ? q.slice(0, -1).trim() : '';
    },
    /** Every option the search matches (the wildcard matches the VALUE's start; text, the label). */
    matchedOptions(s) {
      const all = this.optionsOf(s);
      const pre = this.wildcardPrefix(s).toLowerCase();
      if (pre) return all.filter(o => String(o.value).toLowerCase().indexOf(pre) === 0);
      const q = this.queryOf(s).toLowerCase();
      if (!q) return all;
      return all.filter(o => String(o.label).toLowerCase().indexOf(q) !== -1);
    },
    shownOptions(s) {
      const list = this.matchedOptions(s);
      const cap = s.renderLimit;
      if (!cap || list.length <= cap) return list;
      if (this.queryOf(s)) return list.slice(0, cap);
      const pin = this.pinned[s.key] || [];
      const isPin = o => pin.indexOf(String(o.value)) !== -1;
      const first = list.filter(isPin);
      return first.concat(list.filter(o => !isPin(o))).slice(0, Math.max(cap, first.length));
    },
    hiddenCount(s) {
      return this.matchedOptions(s).length - this.shownOptions(s).length;
    },
    /** What "Add" would add: the typed value when it passes `pattern` and nothing matches it. */
    createText(s) {
      const c = s.creatable;
      if (!c || this.wildcardPrefix(s)) return '';
      let q = this.queryOf(s);
      if (c.uppercase) q = q.toUpperCase();
      if (!q || (c.pattern && !c.pattern.test(q))) return '';
      if (this.optionsOf(s).some(o => String(o.value).toLowerCase() === q.toLowerCase())) return '';
      return this.matchedOptions(s).length ? '' : q;
    },
    createOption(s) {
      const v = this.createText(s);
      if (!v) return;
      this.$set(this.added, s.key, (this.added[s.key] || []).concat([{ value: v, label: v }]));
      this.$set(this.pinned, s.key, (this.pinned[s.key] || []).concat([v]));
      if (!this.isPicked(s.key, v)) this.draft[s.key].selected.push(v);
      this.$set(this.queries, s.key, '');
    },
    pickWildcard(s) {
      this.matchedOptions(s).forEach((o) => { if (!this.isPicked(s.key, o.value)) this.draft[s.key].selected.push(o.value); });
      this.$set(this.queries, s.key, '');
    },
    onSearchEnter(s) {
      if (this.wildcardPrefix(s)) this.pickWildcard(s);
      else this.createOption(s);
    },
    groupsOf(s) {
      const mode = this.draft[s.key] ? this.draft[s.key].mode : null;
      return (s.groups || []).filter(g => g && Array.isArray(g.values) && g.values.length && (!g.mode || g.mode === mode));
    },
    groupOn(key, g) {
      return g.values.every(v => this.isPicked(key, v));
    },
    toggleGroup(key, g) {
      const d = this.draft[key];
      if (this.groupOn(key, g)) {
        d.selected = d.selected.filter(x => !g.values.some(v => String(v) === String(x)));
      } else {
        g.values.forEach((v) => { if (!this.isPicked(key, v)) d.selected.push(v); });
      }
    },
    isPicked(key, v) {
      return this.draft[key].selected.some(x => String(x) === String(v));
    },
    togglePick(key, v) {
      const list = this.draft[key].selected;
      const i = list.findIndex(x => String(x) === String(v));
      if (i === -1) list.push(v);
      else list.splice(i, 1);
    },
    reset() {
      this.draft = copyValue(this.sections, {});
      this.queries = {};
      this.added = {};
    },
    apply() {
      if (this.hasErrors) return;
      const out = {};
      this.sections.forEach((s) => {
        const d = this.draft[s.key];
        // Ticked options in the options' own order (typed ones last, in the order they were added).
        const selected = s.options ? this.optionsOf(s).filter(o => this.isPicked(s.key, o.value)).map(o => o.value) : [];
        out[s.key] = { text: d.text, mode: d.mode, selected };
      });
      this.$emit('apply', out);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-more__scrim {
  @include fu-scrim(true);
  background: rgba(12, 15, 14, 0.42);
}

.fu-more {
  @include fu-font-base;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: $fu-z-popover;
  display: flex;
  flex-direction: column;
  width: 440px;
  max-width: 95%;
  background: $fu-surface;
  box-shadow: $fu-shadow-xl;
  animation: fu-more-in 0.28s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes fu-more-in {
  from { transform: translateX(24px); opacity: 0; }
  to { transform: none; opacity: 1; }
}

.fu-more__head {
  display: flex;
  align-items: flex-start;
  gap: $fu-space-3;
  padding: $fu-space-5 $fu-space-6 $fu-space-4;
  border-bottom: 1px solid $fu-border;
}

.fu-more__titles {
  flex: 1;
  min-width: 0;
}

.fu-more__title {
  @include fu-font-display;
  margin: 0;
  font-weight: 800;
  font-size: 21px;
  letter-spacing: -0.02em;
  color: $fu-text;
}

.fu-more__subtitle {
  margin: 3px 0 0;
  font-size: 13px;
  color: $fu-text-muted;
}

.fu-more__close {
  @include fu-icon-btn(32px);
}

.fu-more__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 0 $fu-space-6;
}

.fu-more__section {
  padding: 18px 0;
  border-bottom: 1px solid $fu-border;
  &:last-child { border-bottom: 0; }
}

.fu-more__section-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fu-more__section-titles {
  flex: 1;
  min-width: 0;
}

.fu-more__section-title {
  font-weight: $fu-fw-bold;
  font-size: 14.5px;
}

.fu-more__hint {
  margin-top: 2px;
  font-size: 12.5px;
  color: $fu-text-muted;
}

.fu-more__modes {
  display: inline-flex;
  flex: none;
  padding: 3px;
  border-radius: $fu-radius-sm + 3px;
  border: 1px solid $fu-border;
  background: $fu-neutral-100;
}

.fu-more__mode {
  @include fu-button-reset;
  @include fu-focus-ring;
  padding: 4px 11px;
  border-radius: $fu-radius-sm + 1px;
  font-size: 12.5px;
  font-weight: $fu-fw-bold;
  color: $fu-text-muted;
}

.fu-more__mode--on {
  background: $fu-surface;
  color: $fu-text;
  box-shadow: $fu-shadow-sm;
}

.fu-more__affix {
  display: inline-flex;
  align-items: center;
  height: 42px;
  margin-top: $fu-space-3;
  border: 1px solid $fu-border-strong;
  border-radius: $fu-radius-md;
  overflow: hidden;
  background: $fu-surface;
  &:focus-within { border-color: $fu-brand-400; box-shadow: $fu-ring; }
}

.fu-more__affix--invalid {
  border-color: $fu-danger;
}

.fu-more__affix-part {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 $fu-space-3;
  font-size: 13.5px;
  color: $fu-text-muted;
  background: $fu-neutral-50;
  border-right: 1px solid $fu-border;
  white-space: nowrap;
}

.fu-more__affix-part--end {
  border-right: 0;
  border-left: 1px solid $fu-border;
}

.fu-more__affix-input {
  width: 90px;
  height: 100%;
  padding: 0 10px;
  border: 0;
  outline: none;
  background: transparent;
  font-family: $fu-font-sans;
  font-size: 14px;
  color: $fu-text;
  &::placeholder { color: $fu-text-subtle; }
  @include fu-phone { font-size: 16px; }
}

.fu-more__error {
  margin-top: 6px;
  font-size: 12.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-danger-text;
}

.fu-more__sr {
  @include fu-visually-hidden;
}

.fu-more__search {
  position: relative;
  display: block;
  margin: $fu-space-3 0 0;
}

.fu-more__search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: $fu-text-muted;
  pointer-events: none;
}

.fu-more__search-input {
  @include fu-input(38px);
  padding-left: 32px;
  font-size: 13.5px;
  &::-webkit-search-cancel-button { -webkit-appearance: none; }
  @include fu-phone { font-size: 16px; }
}

.fu-more__groups {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: $fu-space-3;
}

.fu-more__group {
  @include fu-button-reset;
  @include fu-focus-ring;
  max-width: 100%;
  padding: 5px 10px;
  border-radius: $fu-radius-sm + 2px;
  border: 1px solid $fu-border;
  font-size: 12.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-neutral-600;
  @include fu-ellipsis;
  &:hover { background: $fu-neutral-50; }
}

.fu-more__group--on {
  border-color: $fu-brand;
  background: $fu-brand-50;
  color: $fu-brand-700;
  &:hover { background: $fu-brand-50; }
}

.fu-more__action {
  @include fu-link-btn;
  margin-top: 10px;
  font-size: 13.5px;
}

.fu-more__options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: $fu-space-3;
}

.fu-more__options--scroll {
  max-height: 264px;
  overflow: auto;
  padding-right: 4px;
}

.fu-more__option {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 13.5px;
  font-weight: $fu-fw-medium;
  cursor: pointer;
}

.fu-more__check {
  @include fu-checkbox;
}

.fu-more__option-label {
  min-width: 0;
  @include fu-ellipsis;
}

.fu-more__count {
  @include fu-tabular;
  margin-left: auto;
  font-size: 12.5px;
  color: $fu-text-subtle;
}

.fu-more__empty {
  font-size: 13px;
  color: $fu-text-muted;
}

.fu-more__foot {
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
  gap: 10px;
  padding: 14px $fu-space-6;
  border-top: 1px solid $fu-border;
  background: $fu-neutral-25;
}

.fu-more__apply { @include fu-btn(primary, md); }
.fu-more__cancel { @include fu-btn(secondary, md); }

.fu-more__reset {
  @include fu-link-btn;
  margin-right: auto;
  font-size: 13px;
}

// Phone: the full screen, and the footer's buttons on the LEFT (agent button corner).
@include fu-phone {
  .fu-more { width: 100%; max-width: none; }
  .fu-more__head { padding: $fu-space-4; }
  .fu-more__body { padding: 0 $fu-space-4; }
  .fu-more__foot {
    flex-direction: row;
    padding: $fu-space-3 84px $fu-space-3 $fu-space-4;
  }
  .fu-more__reset { margin-right: 0; margin-left: $fu-space-2; }
}
</style>
